// GstPaymentApproval.js
// Dashboard to approve/reject vendor's GST payment requests raised against a Service Order.
// A rejected request does NOT close the door - the SO raiser can raise the GST payment
// again for the same SO (enforced on the backend, reflected here via a fresh row per raise).

import React, { useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import {
  Paper, Table, TableBody, TableCell, TableContainer,
  TableRow, TableHead, Button, TextField, Typography,
  Select, MenuItem, CircularProgress, Box,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  FormControl, InputLabel, TablePagination
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { FaDownload } from 'react-icons/fa';
import moment from 'moment';
import HttpService from '../../services/httpService';
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';

const useStyles = makeStyles(() => ({
  table: { minWidth: 850 },
  noDataContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px',
    fontSize: '18px',
    color: '#666'
  }
}));

const StyledTableCell = withStyles((theme) => ({
  head: { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText },
  body: { fontSize: 14 }
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: { '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }
}))(TableRow);

const GstPaymentApproval = (props) => {
  const classes = useStyles();
  const { userDetails } = useSelector((state) => state.user);

  const [gstPayments, setGstPayments] = useState([]);
  // pending requests are what this dashboard is opened for, so start there
  const [statusFilter, setStatusFilter] = useState('Raised');
  const [page, setPage] = useState(0);            // 0 based, as TablePagination wants it
  const [rowsPerPage, setRowsPerPage] = useState(200);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoadingState] = useState(true);
  const [rejectReason, setRejectReason] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: '',
    payment: null,
    title: '',
    baseMessage: ''
  });

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [statusFilter, page, rowsPerPage]);

  const getData = async () => {
    setLoadingState(true);
    try {
      const result = await HttpService.getSoGstPaymentApproverRequest(
        statusFilter === 'All' ? '' : statusFilter,
        page + 1,
        rowsPerPage
      );
      if (result.status === 200) {
        const rows = result.data?.data || [];
        const count = result.data?.total || 0;
        setGstPayments(rows);
        setTotalRows(count);
        // approving/rejecting can empty the last page - step back onto a valid one
        if (rows.length === 0 && count > 0 && page > 0) {
          setPage(Math.max(Math.ceil(count / rowsPerPage) - 1, 0));
        }
      }
    } catch (error) {
      console.error(error);
      setGstPayments([]);
      setTotalRows(0);
      props.setSnackInfo(error?.response?.data ?? 'Error fetching GST payment requests', 'error');
    } finally {
      setLoadingState(false);
    }
  };

  const handleStatusChange = (value) => {
    setPage(0);
    setStatusFilter(value);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showConfirmDialog = (action, payment) => {
    const isApprove = action === 'Approved';
    setRejectReason('');
    setConfirmDialog({
      open: true,
      action,
      payment,
      title: `Confirm ${isApprove ? 'Approval' : 'Rejection'}`,
      baseMessage: `Are you sure you want to ${isApprove ? 'approve' : 'reject'} the GST payment of ₹${payment.amount} for ${payment.orderId} (Invoice: ${payment.invoiceNumber})?`
    });
  };

  const handleCancelAction = () => {
    setConfirmDialog({ open: false, action: '', payment: null, title: '', baseMessage: '' });
  };

  const handleConfirmAction = async () => {
    const { action, payment } = confirmDialog;
    if (action === 'Rejected' && (!rejectReason.trim() || rejectReason.trim().length <= 10)) {
      props.setSnackInfo('Provide a valid reason for rejection (more than 10 characters)', 'warning');
      return;
    }
    setConfirmDialog({ open: false, action: '', payment: null, title: '', baseMessage: '' });
    setLoadingState(true);
    try {
      const body = { status: action };
      if (action === 'Rejected') body.rejectReason = rejectReason;
      const response = await HttpService.approveRejectSoGstPaymentRequest(payment._id, body);
      if (response.status === 200) {
        props.setSnackInfo(`GST Payment ${action}`, 'success');
        await getData();
      }
    } catch (error) {
      console.error(error);
      props.setSnackInfo(error?.response?.data ?? 'Something went wrong', 'error');
    } finally {
      setLoadingState(false);
    }
  };

  const isApprover = !!userDetails; // dashboard is access-gated by the route itself (PrivateRoute)

  return (
    <div>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap">
        <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold' }}>
          Vendor's GST Payment Approval — Service Orders
        </Typography>
        <FormControl style={{ minWidth: 200 }} size="small">
          <InputLabel id="gst-payment-status-label">Status</InputLabel>
          <Select
            labelId="gst-payment-status-label"
            value={statusFilter}
            label="Status"
            onChange={(e) => handleStatusChange(e.target.value)}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Raised">Raised (Pending)</MenuItem>
            <MenuItem value="Approved">Approved</MenuItem>
            <MenuItem value="Rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="300px">
          <CircularProgress size={50} />
          <Typography style={{ marginTop: 16, color: '#666' }}>Loading GST payment requests...</Typography>
        </Box>
      ) : gstPayments.length > 0 ? (
        <TableContainer component={Paper}>
          <Table className={classes.table} size="small" aria-label="gst payment approval table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Sl No</StyledTableCell>
                <StyledTableCell>SO Id</StyledTableCell>
                <StyledTableCell>Client & Code</StyledTableCell>
                <StyledTableCell>Vendor</StyledTableCell>
                <StyledTableCell>Invoice No/ID</StyledTableCell>
                <StyledTableCell>Invoice</StyledTableCell>
                <StyledTableCell>GST Challan</StyledTableCell>
                <StyledTableCell>SO Total</StyledTableCell>
                <StyledTableCell>GST Rate</StyledTableCell>
                <StyledTableCell>GST Amount</StyledTableCell>
                <StyledTableCell>Raised By</StyledTableCell>
                <StyledTableCell>Raised At</StyledTableCell>
                <StyledTableCell>Remarks</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gstPayments.map((payment, i) => (
                <StyledTableRow key={payment._id || i}>
                  <StyledTableCell>{page * rowsPerPage + i + 1}</StyledTableCell>
                  <StyledTableCell>{payment?.orderId}</StyledTableCell>
                  <StyledTableCell>{payment?.clientName}_{payment?.projectCode}</StyledTableCell>
                  <StyledTableCell>{payment?.vendorName || '-'}</StyledTableCell>
                  <StyledTableCell>{payment?.invoiceNumber}</StyledTableCell>
                  <StyledTableCell>
                    {(payment?.attachment || []).map((file, idx) => (
                      <span key={idx} style={{ cursor: 'pointer', marginRight: 8 }} onClick={() => window.open(file)}>
                        <FaDownload color="green" />
                      </span>
                    ))}
                  </StyledTableCell>
                  <StyledTableCell>
                    {(payment?.gstChallan || []).length
                      ? payment.gstChallan.map((file, idx) => (
                        <span key={idx} style={{ cursor: 'pointer', marginRight: 8 }} onClick={() => window.open(file)}>
                          <FaDownload color="green" />
                        </span>
                      ))
                      : '-'}
                  </StyledTableCell>
                  <StyledTableCell>₹{payment?.soTotal}</StyledTableCell>
                  <StyledTableCell>{payment?.gstRate}%</StyledTableCell>
                  <StyledTableCell>₹{payment?.amount}</StyledTableCell>
                  <StyledTableCell>{payment?.raisedBy?.name || '-'}</StyledTableCell>
                  <StyledTableCell>
                    {payment?.raisedAt ? moment(payment.raisedAt).format('DD/MM/YYYY hh:mm A') : '--'}
                  </StyledTableCell>
                  <StyledTableCell>{payment?.remarks || '-'}</StyledTableCell>
                  <StyledTableCell>
                    {payment?.status}
                    {payment?.status === 'Rejected' && payment?.rejectReason && (
                      <Typography variant="caption" display="block" color="error">
                        {payment.rejectReason}
                      </Typography>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    {payment?.status === 'Raised' && isApprover ? (
                      <div className="approve-reject-btn">
                        <button onClick={() => showConfirmDialog('Approved', payment)} className="approve-btn">
                          Approve
                        </button>
                        <button onClick={() => showConfirmDialog('Rejected', payment)} className="reject-btn">
                          Reject
                        </button>
                      </div>
                    ) : (
                      <Typography variant="caption" color="textSecondary">-</Typography>
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            rowsPerPageOptions={[100, 200, 500]}
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
          />
        </TableContainer>
      ) : (
        <div className={classes.noDataContainer}>
          No {statusFilter === 'All' ? '' : statusFilter.toLowerCase()} GST payment requests found.
        </div>
      )}

      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelAction}
        aria-labelledby="gst-confirmation-dialog-title"
      >
        <DialogTitle id="gst-confirmation-dialog-title">{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.baseMessage}</DialogContentText>
          {confirmDialog.action === 'Rejected' && (
            <TextField
              autoFocus
              margin="dense"
              label="Reason for Rejection"
              type="text"
              fullWidth
              variant="standard"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              helperText="The SO raiser will be able to raise the GST payment again after this."
              required
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction} color="default">Cancel</Button>
          <Button
            onClick={handleConfirmAction}
            color={confirmDialog.action === 'Approved' ? 'primary' : 'secondary'}
            variant="contained"
            autoFocus
          >
            {confirmDialog.action === 'Approved' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default connect(null, { setLoading, setSnackInfo })(GstPaymentApproval);
